import {
  useCanvasUi,
  type HtmlBlockFocus,
} from "@/pages/canvas/context/CanvasUiContext";
import { useStoreApi } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef } from "react";

export type HtmlBlockFocusMode = HtmlBlockFocus["mode"];

export interface UseHtmlBlockFocusParams {
  nodeId: string;
  /**
   * The block advertises interactivity and is ready. Deliberately NOT gated
   * on `canEdit`: viewers of a published canvas can enter interactive blocks.
   */
  canInteract: boolean;
  /** Editing requires write access, unlike interaction. */
  canEnterEdit: boolean;
  selected: boolean;
  dragging: boolean;
  /**
   * Fires on the FALLING edge of this node's focus — every exit path, latch
   * steal by another block included. The edit session flushes pending island
   * saves here, which is why it is a single callback rather than a check
   * repeated at each exit site.
   */
  onModeEnd?: (previousMode: HtmlBlockFocusMode) => void;
}

export interface UseHtmlBlockFocusResult {
  /** This node's slice of the canvas-wide latch. */
  mode: HtmlBlockFocusMode | null;
  enterInteract: () => void;
  enterEdit: () => void;
  /** Release the latch — a no-op unless this node currently holds it. */
  exit: () => void;
}

/**
 * Per-node view of the canvas-wide block-focus latch.
 *
 * Interaction and editing are the same kind of thing — a transient, per-user
 * claim on one block's pointer — so they share one latch and one set of exit
 * triggers: deselection (click-away, marquee, another node), drag start, the
 * hand tool, Escape in the parent document, losing the capability that
 * justified the mode, unmount, and being stolen by another block.
 *
 * Escape pressed INSIDE the iframe can't be seen here (an activated block
 * holds keyboard focus in another document); the runtime posts an
 * `exit-request` message instead, which the node routes to `exit()`.
 */
export function useHtmlBlockFocus({
  nodeId,
  canInteract,
  canEnterEdit,
  selected,
  dragging,
  onModeEnd,
}: UseHtmlBlockFocusParams): UseHtmlBlockFocusResult {
  const { blockFocus, setBlockFocus, cursorTool } = useCanvasUi();
  const storeApi = useStoreApi();

  const mode = blockFocus?.nodeId === nodeId ? blockFocus.mode : null;

  const modeRef = useRef<HtmlBlockFocusMode | null>(mode);
  const onModeEndRef = useRef(onModeEnd);
  onModeEndRef.current = onModeEnd;

  // Falling edge, observed rather than fired at each exit site — this also
  // catches the case no exit path can see: another block stealing the latch.
  useEffect(() => {
    const previous = modeRef.current;
    modeRef.current = mode;
    if (previous && previous !== mode) {
      onModeEndRef.current?.(previous);
    }
  }, [mode]);

  const exit = useCallback(() => {
    setBlockFocus((prev) => (prev?.nodeId === nodeId ? null : prev));
  }, [nodeId, setBlockFocus]);

  const enter = useCallback(
    (next: HtmlBlockFocusMode) => {
      // Keep the node selected so deselection stays a reliable exit signal
      // and the toolbar stays reachable while inside the block.
      storeApi.getState().addSelectedNodes([nodeId]);
      setBlockFocus({ nodeId, mode: next });
    },
    [nodeId, setBlockFocus, storeApi],
  );

  const enterInteract = useCallback(() => {
    if (!canInteract) return;
    enter("interact");
  }, [canInteract, enter]);

  const enterEdit = useCallback(() => {
    if (!canEnterEdit) return;
    enter("edit");
  }, [canEnterEdit, enter]);

  // The capability that justified this mode went away (interactivity toggled
  // off, edit permission lost, node started loading).
  useEffect(() => {
    if (mode === "interact" && !canInteract) exit();
    if (mode === "edit" && !canEnterEdit) exit();
  }, [mode, canInteract, canEnterEdit, exit]);

  // Canvas gestures reclaim the pointer.
  useEffect(() => {
    if (!mode) return;
    if (!selected || dragging || cursorTool === "hand") exit();
  }, [mode, selected, dragging, cursorTool, exit]);

  // Escape with focus in the parent document.
  useEffect(() => {
    if (!mode) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") exit();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [mode, exit]);

  // Unmount / node deletion / display-mode conversion: release the latch so
  // it can't strand, and fire the falling edge manually — the observer effect
  // above never runs again after unmount, so without this a mid-session
  // unmount would silently drop pending island saves.
  useEffect(() => {
    return () => {
      const previous = modeRef.current;
      if (previous) {
        modeRef.current = null;
        setBlockFocus((prev) => (prev?.nodeId === nodeId ? null : prev));
        onModeEndRef.current?.(previous);
      }
    };
  }, [nodeId, setBlockFocus]);

  return useMemo(
    () => ({ mode, enterInteract, enterEdit, exit }),
    [mode, enterInteract, enterEdit, exit],
  );
}
